import React, { FC, ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import Paper from '@material-ui/core/Paper'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import CircularProgress from '@material-ui/core/CircularProgress'
import { Link } from 'react-router-dom'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, ReactState } from '../../../../React/Common/Contexts'
import { Permissions } from '../../../../React/Common/Contexts/UserInfoContext'
import { UserInfo, useUserInfo } from '../../../Hooks/useUserInfo'
import { PageTitle } from '../../Molecules/PageTitle/PageTitle'
import { AdminFilters, AdminFilterValues, AdminFiltersLocaleText } from '../../Molecules/AdminFilters/AdminFilters'
import {
    ADMIN_URL_QUERY_CME,
    ADMIN_URL_QUERY_INCLUDE_CME_CHILDREN,
    ADMIN_URL_QUERY_PARTITION,
    ADMIN_URL_QUERY_SEARCH,
    ADMIN_URL_QUERY_TELEPHONY_SERVER,
    AdminListUrlBootstrap,
    buildAdminListPathWithQuery,
    parseAdminListUrlSearchParams
} from '../../../Utils/adminListUrlParams'
import { hasResolvedInitialTelSysPartitionListFilters } from '../../../Utils/adminListFilterReadiness'
import {
    AgentListLanguage,
    CmeListLanguage,
    CodeListLanguage,
    DeviceListLanguage,
    DirectoryListLanguage,
    GenericAdminFilterLanguage,
    RecordingListLanguage,
    Router as RouterLang,
    SidebarMenu as SidebarMenuLanguage,
    TelsysListLanguage,
    UsersListLanguage,
    PartitionListLanguage,
    AdminOverviewLanguage
} from '../../../../../i18n'
import {
    ALL_VALUE,
    getAgentList,
    getCmeList,
    getCodeList,
    getDeviceList,
    getDirectoryEntryList,
    getDirectoryEntryListForCME,
    getPartitionList,
    getRecordingList,
    getTelsysList,
    getUsersList
} from '../../../../React/Common/API/ItemsGetters'
import { IsVarEmpty, isStringNotEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { EMPTY_STRING } from '../../../../React/Common/Constants/Separators'
import { RouteID } from '../../Molecules/Header/Types/Types'
import Icon, { IconName } from '../../../../React/Common/Components/Icon'
import { IconSize } from '../../../../NonReact/constants/React/Icon/Constants'
import {
    AdminOverviewTileAdditionalInfo,
    AdminOverviewTileID,
    AdminOverviewTileState,
    AdminOverviewTileViewModel
} from './Types'
import { AdminUserResponse } from '../../../../NonReact/common/Types/Types'
import { hasPermission } from '../../../../React/Common/Utils/PermissionsUtil'
import { CmeListItem, CmeListResponse } from '../../../../NonReact/Types/CmeListTypes'
import { TelsysListItem, TelsysListResponse } from '../../../../NonReact/Types/TelSysListTypes'
import { PartitionListItem, PartitionListResponse } from '../../../../NonReact/Types/PartitionListTypes'
import { DeviceListItem, DeviceListResponse } from '../../../../NonReact/Types/DeviceListTypes'
import { DeviceTypeApiString } from '../../../../NonReact/constants/DeviceTypes'
import { AgentListResponse } from '../../../../NonReact/Types/AgentListTypes'
import { DirectoryListItem, DirectoryListResponse } from '../../../../NonReact/Types/DirectoryListTypes'
import { CodeListResponse } from '../../../../NonReact/Types/CodeListTypes'
import { RecordingListItem, RecordingListResponse } from '../../../../NonReact/Types/RecordingListTypes'
import { UsersListResponse } from '../../../../NonReact/Types/UsersListTypes'

const TYPOGRAPHY_COLOR_TEXT_SECONDARY = 'textSecondary' as const

type CountResponse = Nullable<{
    Data?: {
        Total?: number
    }
}>

type ScopeValues = {
    readonly cmeId: string
    readonly includeCmeChildren: boolean
    readonly search: string
    readonly telephonyServer: Definable<string>
    readonly partition: Definable<string>
    readonly noTelephonyServer: boolean
}

type TileResult = {
    readonly state: AdminOverviewTileState
    readonly value: Definable<string>
    readonly summary: string
    readonly additionalInfo: readonly AdminOverviewTileAdditionalInfo[]
}

type TileResults = Record<AdminOverviewTileID, TileResult>
type TileAccess = Record<AdminOverviewTileID, boolean>
type AdminOverviewFilterOptions = {
    readonly cme: boolean
    readonly telephonyServer: boolean
    readonly partition: boolean
    readonly crmType: boolean
    readonly scope: boolean
    readonly codeType: boolean
    readonly userType: boolean
    readonly includeCmeChildren: boolean
    readonly search: boolean
    readonly includeRefresh: boolean
}

const BOOLEAN_TRUE: string = 'true' as const
const BOOLEAN_FALSE: string = 'false' as const
const TILE_SPINNER_SIZE: number = 40
const TILE_SPINNER_THICKNESS: number = 4
const TILE_GRID_SPACING = 3 as const
const TILE_GRID_XS = 12 as const
const TILE_GRID_SM = 6 as const
const TILE_GRID_MD = 4 as const
const TILE_LINK_CLASS_NAME: string = 'admin-overview-page__tile-link'
const TILE_DISABLED_CLASS_NAME: string = ' admin-overview-page__tile--disabled'
const OVERVIEW_DETAIL_LIMIT: number = 200
const MAX_TILE_ADDITIONAL_INFO_ITEMS: number = 2
const GROUP_DEVICE_API_STRINGS: ReadonlySet<string> = new Set<string>([
    DeviceTypeApiString.GroupSuper,
    DeviceTypeApiString.GroupCollective,
    DeviceTypeApiString.GroupLinear,
    DeviceTypeApiString.GroupCyclic,
    DeviceTypeApiString.GroupAcd,
    DeviceTypeApiString.GroupTrunk,
    DeviceTypeApiString.GroupVoicemail,
    DeviceTypeApiString.GroupPage,
    DeviceTypeApiString.GroupDepartment,
    DeviceTypeApiString.GroupSecurityGroup,
    DeviceTypeApiString.CallQueue
])
const ADMIN_OVERVIEW_FILTER_OPTIONS: AdminOverviewFilterOptions = {
    cme: true,
    telephonyServer: true,
    partition: true,
    crmType: false,
    scope: false,
    codeType: false,
    userType: false,
    includeCmeChildren: true,
    search: true,
    includeRefresh: true
}

const buildTileResult = (
    state: AdminOverviewTileState,
    summary: string,
    value?: Definable<string>,
    additionalInfo: readonly AdminOverviewTileAdditionalInfo[] = []
): TileResult => ({
    state,
    summary,
    value,
    additionalInfo
})

const defaultTileResults = (language: CLanguageHandler): TileResults => ({
    [AdminOverviewTileID.CME]: buildTileResult(
        AdminOverviewTileState.LOADING,
        language.get(AdminOverviewLanguage.AdminOverview_State_Loading)
    ),
    [AdminOverviewTileID.TELSYS]: buildTileResult(
        AdminOverviewTileState.LOADING,
        language.get(AdminOverviewLanguage.AdminOverview_State_Loading)
    ),
    [AdminOverviewTileID.PARTITIONS]: buildTileResult(
        AdminOverviewTileState.LOADING,
        language.get(AdminOverviewLanguage.AdminOverview_State_Loading)
    ),
    [AdminOverviewTileID.USERS]: buildTileResult(
        AdminOverviewTileState.LOADING,
        language.get(AdminOverviewLanguage.AdminOverview_State_Loading)
    ),
    [AdminOverviewTileID.DEVICES]: buildTileResult(
        AdminOverviewTileState.LOADING,
        language.get(AdminOverviewLanguage.AdminOverview_State_Loading)
    ),
    [AdminOverviewTileID.AGENTS]: buildTileResult(
        AdminOverviewTileState.LOADING,
        language.get(AdminOverviewLanguage.AdminOverview_State_Loading)
    ),
    [AdminOverviewTileID.DIRECTORY]: buildTileResult(
        AdminOverviewTileState.LOADING,
        language.get(AdminOverviewLanguage.AdminOverview_State_Loading)
    ),
    [AdminOverviewTileID.CODES]: buildTileResult(
        AdminOverviewTileState.LOADING,
        language.get(AdminOverviewLanguage.AdminOverview_State_Loading)
    ),
    [AdminOverviewTileID.RECORDING]: buildTileResult(
        AdminOverviewTileState.LOADING,
        language.get(AdminOverviewLanguage.AdminOverview_State_Loading)
    )
})

const isConcreteTelSysOrPartitionFilterValue = (value: unknown): boolean => {
    if (IsVarEmpty(value)) {
        return false
    }

    if (value === ALL_VALUE) return false

    return true
}

const getResponseTotal = (response: CountResponse): string => String(response?.Data?.Total ?? 0)

const getNoTelephonyServerSummary = (language: CLanguageHandler): string =>
    language.get(AdminOverviewLanguage.AdminOverview_State_NoTelephonyServer)

const getOptionalSearch = (search: string): Definable<string> => {
    if (search === EMPTY_STRING) return undefined

    return search
}

const getResponseItems = <TItem,>(response: Nullable<{ Data?: { Items?: TItem[] } }>): TItem[] =>
    response?.Data?.Items ?? []

const getResponseTotalNumber = (response: Nullable<{ Data?: { Total?: number } }>): number => response?.Data?.Total ?? 0

const hasLoadedFullResponse = <TItem,>(response: Nullable<{ Data?: { Total?: number; Items?: TItem[] } }>): boolean =>
    getResponseTotalNumber(response) <= getResponseItems(response).length

const buildAdditionalInfoItem = (label: string, value: string | number): AdminOverviewTileAdditionalInfo => ({
    label,
    value: String(value)
})

const getTopGroupedAdditionalInfo = <TItem,>(
    items: readonly TItem[],
    getGroupLabel: (item: TItem) => Definable<string>,
    maxItems: Definable<number> = MAX_TILE_ADDITIONAL_INFO_ITEMS
): readonly AdminOverviewTileAdditionalInfo[] => {
    const groupedCounts: Map<string, number> = items.reduce(
        (counts: Map<string, number>, item: TItem): Map<string, number> => {
            const label: Definable<string> = getGroupLabel(item)

            if (IsVarEmpty(label) || !isStringNotEmpty(label)) {
                return counts
            }

            const safeLabel: string = label
            const currentCount: number = counts.get(safeLabel) ?? 0
            counts.set(safeLabel, currentCount + 1)

            return counts
        },
        new Map<string, number>()
    )

    const groupedAdditionalInfo: AdminOverviewTileAdditionalInfo[] = [...groupedCounts.entries()]
        .sort((left: [string, number], right: [string, number]): number => {
            if (right[1] !== left[1]) return right[1] - left[1]

            return left[0].localeCompare(right[0])
        })
        .map(
            ([label, value]: [string, number]): AdminOverviewTileAdditionalInfo => buildAdditionalInfoItem(label, value)
        )

    if (IsVarEmpty(maxItems)) {
        return groupedAdditionalInfo
    }

    return groupedAdditionalInfo.slice(0, maxItems)
}

const sumNumericItemField = <TItem,>(
    items: readonly TItem[],
    getFieldValue: (item: TItem) => Definable<string | number>
): number =>
    items.reduce((total: number, item: TItem): number => {
        const fieldValue: Definable<string | number> = getFieldValue(item)

        if (IsVarEmpty(fieldValue)) return total

        const numericValue: number = Number(fieldValue)

        if (Number.isNaN(numericValue)) {
            return total
        }

        return total + numericValue
    }, 0)

const buildScopeAdditionalInfo = (
    filters: Nullable<AdminFilterValues>,
    localeText: AdminFiltersLocaleText,
    language: CLanguageHandler,
    noTelephonyServer: boolean
): readonly AdminOverviewTileAdditionalInfo[] => {
    const additionalInfo: AdminOverviewTileAdditionalInfo[] = []

    if (noTelephonyServer) {
        additionalInfo.push(
            buildAdditionalInfoItem(
                localeText.telephonyServerLabel ?? EMPTY_STRING,
                getNoTelephonyServerSummary(language)
            )
        )
    } else if (
        filters?.telephonyServer?.value !== ALL_VALUE &&
        isStringNotEmpty(filters?.telephonyServer?.label ?? EMPTY_STRING)
    ) {
        additionalInfo.push(
            buildAdditionalInfoItem(
                localeText.telephonyServerLabel ?? EMPTY_STRING,
                filters?.telephonyServer?.label ?? EMPTY_STRING
            )
        )
    }

    if (
        filters?.partition?.value !== ALL_VALUE &&
        isStringNotEmpty(filters?.partition?.label ?? EMPTY_STRING) &&
        isStringNotEmpty(localeText.partitionLabel ?? EMPTY_STRING)
    ) {
        additionalInfo.push(
            buildAdditionalInfoItem(
                localeText.partitionLabel ?? EMPTY_STRING,
                filters?.partition?.label ?? EMPTY_STRING
            )
        )
    }

    if (isStringNotEmpty(filters?.search ?? EMPTY_STRING) && isStringNotEmpty(localeText.searchLabel ?? EMPTY_STRING)) {
        additionalInfo.push(
            buildAdditionalInfoItem(localeText.searchLabel ?? EMPTY_STRING, filters?.search ?? EMPTY_STRING)
        )
    }

    return additionalInfo.slice(0, MAX_TILE_ADDITIONAL_INFO_ITEMS)
}

const buildScopedQuery = (scopeValues: ScopeValues): Record<string, Definable<string>> => ({
    [ADMIN_URL_QUERY_CME]: scopeValues.cmeId,
    [ADMIN_URL_QUERY_INCLUDE_CME_CHILDREN]: scopeValues.includeCmeChildren ? BOOLEAN_TRUE : BOOLEAN_FALSE,
    [ADMIN_URL_QUERY_TELEPHONY_SERVER]: scopeValues.telephonyServer,
    [ADMIN_URL_QUERY_PARTITION]: scopeValues.partition,
    [ADMIN_URL_QUERY_SEARCH]: getOptionalSearch(scopeValues.search)
})

const useOverviewScopeValues = (
    filters: Nullable<AdminFilterValues>,
    userInfo: Nullable<AdminUserResponse>
): ScopeValues => {
    const cmeFilterValue: Definable<string | number> = filters?.cme?.value
    const telephonyServerValue: Definable<string | number> = filters?.telephonyServer?.value
    const partitionValue: Definable<string | number> = filters?.partition?.value

    let telephonyServer: Definable<string>

    if (isConcreteTelSysOrPartitionFilterValue(telephonyServerValue)) {
        telephonyServer = String(telephonyServerValue)
    }

    let partition: Definable<string>

    if (isVarNotEmpty(telephonyServer) && isConcreteTelSysOrPartitionFilterValue(partitionValue)) {
        partition = String(partitionValue)
    }

    let cmeId: string = String(cmeFilterValue)

    if (IsVarEmpty(cmeFilterValue)) {
        cmeId = String(userInfo?.Scope?.CMEnvironmentID ?? EMPTY_STRING)
    }

    return {
        cmeId,
        includeCmeChildren: filters?.includeCmeChildren ?? true,
        search: filters?.search ?? EMPTY_STRING,
        telephonyServer,
        partition,
        noTelephonyServer: filters?.noTelephonyServer ?? false
    }
}

const getDirectoryRequest = (scopeValues: ScopeValues): Promise<Nullable<DirectoryListResponse>> => {
    if (scopeValues.noTelephonyServer) return Promise.resolve(null)

    if (isVarNotEmpty(scopeValues.telephonyServer) && isVarNotEmpty(scopeValues.partition)) {
        return getDirectoryEntryList(
            scopeValues.telephonyServer,
            scopeValues.partition,
            0,
            OVERVIEW_DETAIL_LIMIT,
            undefined,
            getOptionalSearch(scopeValues.search)
        )
    }

    return getDirectoryEntryListForCME(
        scopeValues.cmeId,
        scopeValues.includeCmeChildren,
        scopeValues.telephonyServer,
        scopeValues.partition,
        0,
        OVERVIEW_DETAIL_LIMIT,
        undefined,
        getOptionalSearch(scopeValues.search)
    )
}

const getAgentRequest = (scopeValues: ScopeValues): Promise<Nullable<AgentListResponse>> => {
    if (IsVarEmpty(scopeValues.telephonyServer) || IsVarEmpty(scopeValues.partition)) return Promise.resolve(null)

    return getAgentList(
        Number(scopeValues.telephonyServer),
        Number(scopeValues.partition),
        false,
        0,
        OVERVIEW_DETAIL_LIMIT,
        undefined,
        getOptionalSearch(scopeValues.search)
    )
}

const getDeviceRequest = (scopeValues: ScopeValues): Promise<Nullable<DeviceListResponse>> => {
    if (IsVarEmpty(scopeValues.telephonyServer) || IsVarEmpty(scopeValues.partition)) return Promise.resolve(null)

    return getDeviceList(
        scopeValues.telephonyServer,
        scopeValues.partition,
        0,
        OVERVIEW_DETAIL_LIMIT,
        undefined,
        getOptionalSearch(scopeValues.search)
    )
}

/**
 * Loads enough device rows to calculate the per-type breakdown for the Devices overview tile.
 *
 * @param scopeValues - Current scoped admin filter values.
 * @returns A response containing all device rows for the current scope when available.
 */
const getDeviceRequestWithAdditionalInfo = async (scopeValues: ScopeValues): Promise<Nullable<DeviceListResponse>> => {
    const initialResponse: Nullable<DeviceListResponse> = await getDeviceRequest(scopeValues)

    if (!initialResponse) return initialResponse

    const totalDevices: number = getResponseTotalNumber(initialResponse)
    const loadedDevices: number = getResponseItems(initialResponse).length

    if (totalDevices <= loadedDevices || totalDevices <= OVERVIEW_DETAIL_LIMIT) return initialResponse

    return getDeviceList(
        scopeValues.telephonyServer ?? EMPTY_STRING,
        scopeValues.partition ?? EMPTY_STRING,
        0,
        totalDevices,
        undefined,
        getOptionalSearch(scopeValues.search)
    )
}

const getPartitionRequest = (scopeValues: ScopeValues): Promise<Nullable<PartitionListResponse>> => {
    if (IsVarEmpty(scopeValues.telephonyServer)) return Promise.resolve(null)

    return getPartitionList(
        scopeValues.telephonyServer,
        0,
        OVERVIEW_DETAIL_LIMIT,
        undefined,
        getOptionalSearch(scopeValues.search)
    )
}

const getUsersRequest = (scopeValues: ScopeValues): Promise<Nullable<UsersListResponse>> => {
    if (scopeValues.noTelephonyServer) return Promise.resolve(null)

    return getUsersList(
        scopeValues.telephonyServer ?? ALL_VALUE,
        scopeValues.partition ?? ALL_VALUE,
        scopeValues.cmeId,
        scopeValues.includeCmeChildren,
        0,
        OVERVIEW_DETAIL_LIMIT,
        undefined,
        getOptionalSearch(scopeValues.search)
    )
}

const getRecordingRequest = (scopeValues: ScopeValues): Promise<Nullable<RecordingListResponse>> => {
    if (scopeValues.noTelephonyServer) return Promise.resolve(null)

    return getRecordingList(
        scopeValues.cmeId,
        scopeValues.includeCmeChildren,
        scopeValues.telephonyServer,
        scopeValues.partition,
        undefined,
        0,
        OVERVIEW_DETAIL_LIMIT,
        undefined,
        getOptionalSearch(scopeValues.search)
    )
}

/**
 * Loads enough recording rows to calculate the per-integration-type breakdown for the Recording overview tile.
 *
 * @param scopeValues - Current scoped admin filter values.
 * @returns A response containing all recording rows for the current scope when available.
 */
const getRecordingRequestWithAdditionalInfo = async (
    scopeValues: ScopeValues
): Promise<Nullable<RecordingListResponse>> => {
    const initialResponse: Nullable<RecordingListResponse> = await getRecordingRequest(scopeValues)

    if (!initialResponse) return initialResponse

    const totalRecordings: number = getResponseTotalNumber(initialResponse)
    const loadedRecordings: number = getResponseItems(initialResponse).length

    if (totalRecordings <= loadedRecordings || totalRecordings <= OVERVIEW_DETAIL_LIMIT) return initialResponse

    return getRecordingList(
        scopeValues.cmeId,
        scopeValues.includeCmeChildren,
        scopeValues.telephonyServer,
        scopeValues.partition,
        undefined,
        0,
        totalRecordings,
        undefined,
        getOptionalSearch(scopeValues.search)
    )
}

const getCmeAdditionalInfo = (
    language: CLanguageHandler,
    response: Nullable<CmeListResponse>
): readonly AdminOverviewTileAdditionalInfo[] => {
    if (!hasLoadedFullResponse(response)) {
        return []
    }

    const items: CmeListItem[] = getResponseItems(response)
    const telephonyServerCount: number = sumNumericItemField(
        items,
        (item: CmeListItem): Definable<string | number> => item.ListTelSyssCount
    )
    const userCount: number = sumNumericItemField(
        items,
        (item: CmeListItem): Definable<string | number> => item.ListUsersCount
    )

    return [
        buildAdditionalInfoItem(
            language.get(AdminOverviewLanguage.AdminOverview_Tile_Telsys_Title),
            telephonyServerCount
        ),
        buildAdditionalInfoItem(language.get(AdminOverviewLanguage.AdminOverview_Tile_Users_Title), userCount)
    ]
}

const getTelsysAdditionalInfo = (
    response: Nullable<TelsysListResponse>
): readonly AdminOverviewTileAdditionalInfo[] => {
    if (!hasLoadedFullResponse(response)) {
        return []
    }

    return getTopGroupedAdditionalInfo(
        getResponseItems(response),
        (item: TelsysListItem): Definable<string> => item.ListTelSysTypeDisplay
    )
}

const getPartitionAdditionalInfo = (
    response: Nullable<PartitionListResponse>
): readonly AdminOverviewTileAdditionalInfo[] => {
    if (!hasLoadedFullResponse(response)) {
        return []
    }

    return getTopGroupedAdditionalInfo(
        getResponseItems(response),
        (item: PartitionListItem): Definable<string> =>
            isStringNotEmpty(item.LicensedForRptUserType ?? EMPTY_STRING) ? item.LicensedForRptUserType : item.SyncType
    )
}

const getDeviceAdditionalInfo = (
    response: Nullable<DeviceListResponse>,
    language: CLanguageHandler
): readonly AdminOverviewTileAdditionalInfo[] => {
    if (!hasLoadedFullResponse(response)) {
        return []
    }

    const items: readonly DeviceListItem[] = getResponseItems(response)

    let extensionCount: number = 0
    let extensionTrunkingCount: number = 0
    let extensionHospitalityCount: number = 0
    let groupCount: number = 0
    let autoAttendantCount: number = 0

    for (const item of items) {
        const deviceType: Definable<string> = item.DeviceType

        if (IsVarEmpty(deviceType)) {
            continue
        }

        if (deviceType === DeviceTypeApiString.ExtensionOrEndpoint) {
            extensionCount++
        } else if (deviceType === DeviceTypeApiString.ExtensionOrEndpointTrunking) {
            extensionTrunkingCount++
        } else if (deviceType === DeviceTypeApiString.ExtensionOrEndpointHospitality) {
            extensionHospitalityCount++
        } else if (GROUP_DEVICE_API_STRINGS.has(deviceType)) {
            groupCount++
        } else if (deviceType === DeviceTypeApiString.AutoAttendant) {
            autoAttendantCount++
        }
    }

    const result: AdminOverviewTileAdditionalInfo[] = []

    if (extensionCount > 0) {
        result.push(buildAdditionalInfoItem(language.get(AdminOverviewLanguage.AdminOverview_Tile_Devices_Extension), extensionCount))
    }

    if (extensionTrunkingCount > 0) {
        result.push(buildAdditionalInfoItem(language.get(AdminOverviewLanguage.AdminOverview_Tile_Devices_ExtensionTrunking), extensionTrunkingCount))
    }

    if (extensionHospitalityCount > 0) {
        result.push(buildAdditionalInfoItem(language.get(AdminOverviewLanguage.AdminOverview_Tile_Devices_ExtensionHospitality), extensionHospitalityCount))
    }

    if (groupCount > 0) {
        result.push(buildAdditionalInfoItem(language.get(AdminOverviewLanguage.AdminOverview_Tile_Devices_Groups), groupCount))
    }

    if (autoAttendantCount > 0) {
        result.push(buildAdditionalInfoItem(language.get(AdminOverviewLanguage.AdminOverview_Tile_Devices_AutoAttendant), autoAttendantCount))
    }

    return result
}


const getDirectoryAdditionalInfo = (
    response: Nullable<DirectoryListResponse>,
    scopeValues: ScopeValues
): readonly AdminOverviewTileAdditionalInfo[] => {
    if (!hasLoadedFullResponse(response)) {
        return []
    }

    const items: DirectoryListItem[] = getResponseItems(response)

    if (isVarNotEmpty(scopeValues.telephonyServer)) {
        return getTopGroupedAdditionalInfo(
            items,
            (item: DirectoryListItem): Definable<string> =>
                IsVarEmpty(item.PartIDInTelSys) ? undefined : String(item.PartIDInTelSys)
        )
    }

    return getTopGroupedAdditionalInfo(
        items,
        (item: DirectoryListItem): Definable<string> =>
            IsVarEmpty(item.TelSysID) ? undefined : String(item.TelSysID)
    )
}

const getRecordingAdditionalInfo = (
    response: Nullable<RecordingListResponse>
): readonly AdminOverviewTileAdditionalInfo[] => {
    if (!hasLoadedFullResponse(response)) {
        return []
    }

    return getTopGroupedAdditionalInfo(
        getResponseItems(response),
        (item: RecordingListItem): Definable<string> => item.CallRecIntTypeDescription
    )
}

const getPartitionTileResult = (
    language: CLanguageHandler,
    scopeValues: ScopeValues,
    response: CountResponse
): TileResult => {
    if (IsVarEmpty(scopeValues.telephonyServer)) {
        return buildTileResult(
            AdminOverviewTileState.NEEDS_SCOPE,
            language.get(AdminOverviewLanguage.AdminOverview_State_SelectTelephonyServer)
        )
    }

    return buildTileResult(
        AdminOverviewTileState.READY,
        language.get(AdminOverviewLanguage.AdminOverview_Tile_Partitions_Summary),
        getResponseTotal(response)
    )
}

const withAdditionalInfo = (
    tileResult: TileResult,
    additionalInfo: readonly AdminOverviewTileAdditionalInfo[]
): TileResult => ({
    ...tileResult,
    additionalInfo
})

const getScopedPartitionRequirementSummary = (language: CLanguageHandler, scopeValues: ScopeValues): string => {
    if (IsVarEmpty(scopeValues.telephonyServer)) return language.get(AdminOverviewLanguage.AdminOverview_State_SelectTelephonyServer)

    return language.get(AdminOverviewLanguage.AdminOverview_State_SelectPartition)
}

const getScopedTelsysPartitionTileResult = (
    language: CLanguageHandler,
    scopeValues: ScopeValues,
    response: CountResponse,
    summaryKey: string
): TileResult => {
    if (IsVarEmpty(scopeValues.telephonyServer) || IsVarEmpty(scopeValues.partition)) {
        return buildTileResult(
            AdminOverviewTileState.NEEDS_SCOPE,
            getScopedPartitionRequirementSummary(language, scopeValues)
        )
    }

    return buildTileResult(AdminOverviewTileState.READY, language.get(summaryKey), getResponseTotal(response))
}

const getNoTelephonyAwareTileResult = (
    language: CLanguageHandler,
    scopeValues: ScopeValues,
    response: CountResponse,
    summaryKey: string
): TileResult => {
    if (scopeValues.noTelephonyServer) return buildTileResult(AdminOverviewTileState.READY, getNoTelephonyServerSummary(language))

    return buildTileResult(AdminOverviewTileState.READY, language.get(summaryKey), getResponseTotal(response))
}

/**
 * Routed Administration overview.
 *
 * @returns Administration overview tiles scoped by the shared admin filters.
 */
export const AdminOverview: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(RouterLang.NAME)
    language.loadBundle(SidebarMenuLanguage.NAME)
    language.loadBundle(GenericAdminFilterLanguage.NAME)
    language.loadBundle(AdminOverviewLanguage.NAME)
    language.loadBundle(CmeListLanguage.NAME)
    language.loadBundle(TelsysListLanguage.NAME)
    language.loadBundle(PartitionListLanguage.NAME)
    language.loadBundle(UsersListLanguage.NAME)
    language.loadBundle(DeviceListLanguage.NAME)
    language.loadBundle(AgentListLanguage.NAME)
    language.loadBundle(DirectoryListLanguage.NAME)
    language.loadBundle(CodeListLanguage.NAME)
    language.loadBundle(RecordingListLanguage.NAME)

    const [userInfo]: UserInfo = useUserInfo()

    const [filters, setFilters]: ReactState<Nullable<AdminFilterValues>> = useState<Nullable<AdminFilterValues>>(null)
    const [hasReceivedStableFilters, setHasReceivedStableFilters]: ReactState<boolean> = useState<boolean>(false)
    const [refreshCount, setRefreshCount]: ReactState<number> = useState<number>(0)
    const [tileResults, setTileResults]: ReactState<TileResults> = useState<TileResults>(() =>
        defaultTileResults(language)
    )

    const urlBootstrap: Nullable<AdminListUrlBootstrap> = useMemo((): Nullable<AdminListUrlBootstrap> => {
        const parsed: AdminListUrlBootstrap = parseAdminListUrlSearchParams(new URLSearchParams(window.location.search))

        return Object.keys(parsed).length > 0 ? parsed : null
    }, [])

    const scopeValues: ScopeValues = useOverviewScopeValues(filters, userInfo)
    const tileAccess: TileAccess = useMemo(
        (): TileAccess => ({
            [AdminOverviewTileID.CME]: hasPermission(userInfo, Permissions.CME_READ),
            [AdminOverviewTileID.TELSYS]: hasPermission(userInfo, Permissions.TEL_SYS_READ),
            [AdminOverviewTileID.PARTITIONS]: hasPermission(userInfo, Permissions.PARTITION_READ),
            [AdminOverviewTileID.USERS]: hasPermission(userInfo, Permissions.USER_READ),
            [AdminOverviewTileID.DEVICES]: hasPermission(userInfo, Permissions.DEVICE_READ),
            [AdminOverviewTileID.AGENTS]: hasPermission(userInfo, Permissions.ACDAGENT_READ),
            [AdminOverviewTileID.DIRECTORY]: hasPermission(userInfo, Permissions.DIRECTORY_ENTRY_READ),
            [AdminOverviewTileID.CODES]: hasPermission(userInfo, Permissions.CODE_READ),
            [AdminOverviewTileID.RECORDING]: hasPermission(userInfo, Permissions.RECORDING_READ)
        }),
        [userInfo]
    )

    const localeText: AdminFiltersLocaleText = useMemo(
        (): AdminFiltersLocaleText => ({
            cmeLabel: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_CME_LABEL),
            telephonyServerLabel: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_TELSYS_LABEL),
            partitionLabel: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_PARTITION_LABEL),
            includeChildCmeLabel: language.get(
                GenericAdminFilterLanguage.GenericAdminFilterLanguage_INCLUDE_CHILD_CMES_LABEL
            ),
            searchLabel: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_SEARCH_LABEL)
        }),
        [language.getLanguageID()]
    )
    const scopedQuery: Record<string, Definable<string>> = useMemo(
        (): Record<string, Definable<string>> => buildScopedQuery(scopeValues),
        [
            scopeValues.cmeId,
            scopeValues.includeCmeChildren,
            scopeValues.partition,
            scopeValues.search,
            scopeValues.telephonyServer
        ]
    )

    const handleFiltersChange = useCallback((values: AdminFilterValues): void => {
        setFilters(values)

        if (hasResolvedInitialTelSysPartitionListFilters(values)) {
            setHasReceivedStableFilters(true)
        }
    }, [])

    const handleRefresh = useCallback((): void => {
        setRefreshCount((count: number): number => count + 1)
    }, [])

    const tiles: AdminOverviewTileViewModel[] = useMemo((): AdminOverviewTileViewModel[] => {
        const fallbackAdditionalInfo: readonly AdminOverviewTileAdditionalInfo[] = buildScopeAdditionalInfo(
            filters,
            localeText,
            language,
            scopeValues.noTelephonyServer
        )

        const getTileAdditionalInfo = (id: AdminOverviewTileID): readonly AdminOverviewTileAdditionalInfo[] => {
            if (tileResults[id].additionalInfo.length > 0) return tileResults[id].additionalInfo

            if (
                id === AdminOverviewTileID.USERS ||
                id === AdminOverviewTileID.DEVICES ||
                id === AdminOverviewTileID.AGENTS ||
                id === AdminOverviewTileID.CODES ||
                id === AdminOverviewTileID.RECORDING
            ) {
                return []
            }

            return fallbackAdditionalInfo
        }

        const routeTile = (
            id: AdminOverviewTileID,
            title: string,
            subtitle: string,
            ariaLabel: string,
            icon: IconName,
            routeID: RouteID,
            visible: boolean = tileAccess[id]
        ): AdminOverviewTileViewModel => ({
            id,
            title,
            subtitle,
            ariaLabel,
            icon,
            visible,
            enabled: tileAccess[id],
            ...tileResults[id],
            additionalInfo: getTileAdditionalInfo(id),
            to: buildAdminListPathWithQuery(`/${routeID}`, scopedQuery)
        })

        const hasTelSysFilter: boolean = isVarNotEmpty(scopeValues.telephonyServer)
        const hasPartitionFilter: boolean = isVarNotEmpty(scopeValues.partition)

        return [
            routeTile(
                AdminOverviewTileID.CME,
                language.get(AdminOverviewLanguage.AdminOverview_Tile_Cme_Title),
                language.get(AdminOverviewLanguage.AdminOverview_Tile_Cme_Subtitle),
                language.get(AdminOverviewLanguage.AdminOverview_Tile_Cme_Aria),
                IconName.CME_SIDEBAR,
                RouteID.CME,
                tileAccess[AdminOverviewTileID.CME] && !hasTelSysFilter
            ),
            routeTile(
                AdminOverviewTileID.TELSYS,
                language.get(AdminOverviewLanguage.AdminOverview_Tile_Telsys_Title),
                language.get(AdminOverviewLanguage.AdminOverview_Tile_Telsys_Subtitle),
                language.get(AdminOverviewLanguage.AdminOverview_Tile_Telsys_Aria),
                IconName.TELSERVERS_SIDEBAR,
                RouteID.TELSYS,
                tileAccess[AdminOverviewTileID.TELSYS] && !hasTelSysFilter
            ),
            routeTile(
                AdminOverviewTileID.PARTITIONS,
                language.get(AdminOverviewLanguage.AdminOverview_Tile_Partitions_Title),
                language.get(AdminOverviewLanguage.AdminOverview_Tile_Partitions_Subtitle),
                language.get(AdminOverviewLanguage.AdminOverview_Tile_Partitions_Aria),
                IconName.PARTITIONS_SIDEBAR,
                RouteID.PARTITION,
                tileAccess[AdminOverviewTileID.PARTITIONS] && !hasPartitionFilter
            ),
            routeTile(
                AdminOverviewTileID.USERS,
                language.get(AdminOverviewLanguage.AdminOverview_Tile_Users_Title),
                language.get(AdminOverviewLanguage.AdminOverview_Tile_Users_Subtitle),
                language.get(AdminOverviewLanguage.AdminOverview_Tile_Users_Aria),
                IconName.USERS_SIDEBAR,
                RouteID.USERS
            ),
            routeTile(
                AdminOverviewTileID.DEVICES,
                language.get(AdminOverviewLanguage.AdminOverview_Tile_Devices_Title),
                language.get(AdminOverviewLanguage.AdminOverview_Tile_Devices_Subtitle),
                language.get(AdminOverviewLanguage.AdminOverview_Tile_Devices_Aria),
                IconName.DEVICES_SIDERBAR,
                RouteID.DEVICE
            ),
            routeTile(
                AdminOverviewTileID.AGENTS,
                language.get(AdminOverviewLanguage.AdminOverview_Tile_Agents_Title),
                language.get(AdminOverviewLanguage.AdminOverview_Tile_Agents_Subtitle),
                language.get(AdminOverviewLanguage.AdminOverview_Tile_Agents_Aria),
                IconName.AGENTS_SIDEBAR,
                RouteID.AGENT
            ),
            routeTile(
                AdminOverviewTileID.DIRECTORY,
                language.get(AdminOverviewLanguage.AdminOverview_Tile_Directory_Title),
                language.get(AdminOverviewLanguage.AdminOverview_Tile_Directory_Subtitle),
                language.get(AdminOverviewLanguage.AdminOverview_Tile_Directory_Aria),
                IconName.DIRECTORY_SIDEBAR,
                RouteID.DIRECTORY
            ),
            routeTile(
                AdminOverviewTileID.CODES,
                language.get(AdminOverviewLanguage.AdminOverview_Tile_Codes_Title),
                language.get(AdminOverviewLanguage.AdminOverview_Tile_Codes_Subtitle),
                language.get(AdminOverviewLanguage.AdminOverview_Tile_Codes_Aria),
                IconName.CODES_SIDEBAR,
                RouteID.CODE
            ),
            routeTile(
                AdminOverviewTileID.RECORDING,
                language.get(AdminOverviewLanguage.AdminOverview_Tile_Recording_Title),
                language.get(AdminOverviewLanguage.AdminOverview_Tile_Recording_Subtitle),
                language.get(AdminOverviewLanguage.AdminOverview_Tile_Recording_Aria),
                IconName.RECORDINGS_SIDEBAR,
                RouteID.RECORDING
            )
        ].filter((tile: AdminOverviewTileViewModel): boolean => tile.visible)
    }, [
        scopedQuery,
        tileResults,
        tileAccess,
        scopeValues.telephonyServer,
        scopeValues.partition,
        scopeValues.noTelephonyServer,
        filters,
        localeText,
        language.getLanguageID()
    ])

    useEffect(() => {
        if (!hasReceivedStableFilters || !isStringNotEmpty(scopeValues.cmeId)) {
            return undefined
        }

        let isActive: boolean = true
        const nextResults: TileResults = {
            ...defaultTileResults(language),
            [AdminOverviewTileID.PARTITIONS]:
                IsVarEmpty(scopeValues.telephonyServer)
                    ? buildTileResult(
                          AdminOverviewTileState.NEEDS_SCOPE,
                          language.get(AdminOverviewLanguage.AdminOverview_State_SelectTelephonyServer)
                      )
                    : defaultTileResults(language)[AdminOverviewTileID.PARTITIONS],
            [AdminOverviewTileID.DEVICES]:
                IsVarEmpty(scopeValues.telephonyServer) || IsVarEmpty(scopeValues.partition)
                    ? buildTileResult(
                          AdminOverviewTileState.NEEDS_SCOPE,
                          getScopedPartitionRequirementSummary(language, scopeValues)
                      )
                    : defaultTileResults(language)[AdminOverviewTileID.DEVICES],
            [AdminOverviewTileID.AGENTS]:
                IsVarEmpty(scopeValues.telephonyServer) || IsVarEmpty(scopeValues.partition)
                    ? buildTileResult(
                          AdminOverviewTileState.NEEDS_SCOPE,
                          getScopedPartitionRequirementSummary(language, scopeValues)
                      )
                    : defaultTileResults(language)[AdminOverviewTileID.AGENTS]
        }

        if (scopeValues.noTelephonyServer) {
            nextResults[AdminOverviewTileID.USERS] = buildTileResult(
                AdminOverviewTileState.READY,
                getNoTelephonyServerSummary(language)
            )
            nextResults[AdminOverviewTileID.DIRECTORY] = buildTileResult(
                AdminOverviewTileState.READY,
                getNoTelephonyServerSummary(language)
            )
            nextResults[AdminOverviewTileID.RECORDING] = buildTileResult(
                AdminOverviewTileState.READY,
                getNoTelephonyServerSummary(language)
            )
        }

        setTileResults(nextResults)

        const requests: Array<Promise<readonly [AdminOverviewTileID, TileResult]>> = [
            ...(tileAccess[AdminOverviewTileID.CME]
                ? [
                      getCmeList(
                          0,
                          OVERVIEW_DETAIL_LIMIT,
                          undefined,
                          getOptionalSearch(scopeValues.search),
                          undefined,
                          scopeValues.cmeId,
                          scopeValues.includeCmeChildren
                      ).then((response: Nullable<CmeListResponse>): readonly [AdminOverviewTileID, TileResult] => [
                          AdminOverviewTileID.CME,
                          buildTileResult(
                              AdminOverviewTileState.READY,
                              language.get(AdminOverviewLanguage.AdminOverview_Tile_Cme_Summary),
                              getResponseTotal(response),
                              getCmeAdditionalInfo(language, response)
                          )
                      ])
                  ]
                : []),
            ...(tileAccess[AdminOverviewTileID.TELSYS]
                ? [
                      getTelsysList(
                          scopeValues.cmeId,
                          scopeValues.includeCmeChildren,
                          0,
                          OVERVIEW_DETAIL_LIMIT,
                          undefined,
                          getOptionalSearch(scopeValues.search)
                      ).then((response: Nullable<TelsysListResponse>): readonly [AdminOverviewTileID, TileResult] => [
                          AdminOverviewTileID.TELSYS,
                          buildTileResult(
                              AdminOverviewTileState.READY,
                              language.get(AdminOverviewLanguage.AdminOverview_Tile_Telsys_Summary),
                              getResponseTotal(response),
                              getTelsysAdditionalInfo(response)
                          )
                      ])
                  ]
                : []),
            ...(tileAccess[AdminOverviewTileID.USERS]
                ? [
                      getUsersRequest(scopeValues).then(
                          (response: Nullable<UsersListResponse>): readonly [AdminOverviewTileID, TileResult] => [
                              AdminOverviewTileID.USERS,
                              getNoTelephonyAwareTileResult(
                                  language,
                                  scopeValues,
                                  response,
                                  AdminOverviewLanguage.AdminOverview_Tile_Users_Summary
                              )
                          ]
                      )
                  ]
                : []),
            ...(tileAccess[AdminOverviewTileID.CODES]
                ? [
                      getCodeList(
                          scopeValues.cmeId,
                          scopeValues.includeCmeChildren,
                          scopeValues.telephonyServer,
                          scopeValues.partition,
                          undefined,
                          0,
                          OVERVIEW_DETAIL_LIMIT,
                          undefined,
                          getOptionalSearch(scopeValues.search)
                      ).then((response: Nullable<CodeListResponse>): readonly [AdminOverviewTileID, TileResult] => [
                          AdminOverviewTileID.CODES,
                          buildTileResult(
                              AdminOverviewTileState.READY,
                              language.get(AdminOverviewLanguage.AdminOverview_Tile_Codes_Summary),
                              getResponseTotal(response)
                          )
                      ])
                  ]
                : []),
            ...(tileAccess[AdminOverviewTileID.RECORDING]
                ? [
                      getRecordingRequestWithAdditionalInfo(scopeValues).then(
                          (response: Nullable<RecordingListResponse>): readonly [AdminOverviewTileID, TileResult] => [
                              AdminOverviewTileID.RECORDING,
                              buildTileResult(
                                  AdminOverviewTileState.READY,
                                  scopeValues.noTelephonyServer
                                      ? getNoTelephonyServerSummary(language)
                                      : language.get(AdminOverviewLanguage.AdminOverview_Tile_Recording_Summary),
                                  scopeValues.noTelephonyServer ? undefined : getResponseTotal(response),
                                  getRecordingAdditionalInfo(response)
                              )
                          ]
                      )
                  ]
                : []),
            ...(tileAccess[AdminOverviewTileID.PARTITIONS]
                ? [
                      getPartitionRequest(scopeValues).then(
                          (response: Nullable<PartitionListResponse>): readonly [AdminOverviewTileID, TileResult] => [
                              AdminOverviewTileID.PARTITIONS,
                              withAdditionalInfo(
                                  getPartitionTileResult(language, scopeValues, response),
                                  getPartitionAdditionalInfo(response)
                              )
                          ]
                      )
                  ]
                : []),
            ...(tileAccess[AdminOverviewTileID.DEVICES]
                ? [
                      getDeviceRequestWithAdditionalInfo(scopeValues).then(
                          (response: Nullable<DeviceListResponse>): readonly [AdminOverviewTileID, TileResult] => [
                              AdminOverviewTileID.DEVICES,
                              withAdditionalInfo(
                                  getScopedTelsysPartitionTileResult(
                                      language,
                                      scopeValues,
                                      response,
                                      AdminOverviewLanguage.AdminOverview_Tile_Devices_Summary
                                  ),
                                  getDeviceAdditionalInfo(response, language)
                              )
                          ]
                      )
                  ]
                : []),
            ...(tileAccess[AdminOverviewTileID.AGENTS]
                ? [
                      getAgentRequest(scopeValues).then(
                          (response: Nullable<AgentListResponse>): readonly [AdminOverviewTileID, TileResult] => [
                              AdminOverviewTileID.AGENTS,
                              getScopedTelsysPartitionTileResult(
                                  language,
                                  scopeValues,
                                  response,
                                  AdminOverviewLanguage.AdminOverview_Tile_Agents_Summary
                              )
                          ]
                      )
                  ]
                : []),
            ...(tileAccess[AdminOverviewTileID.DIRECTORY]
                ? [
                      getDirectoryRequest(scopeValues).then(
                          (response: Nullable<DirectoryListResponse>): readonly [AdminOverviewTileID, TileResult] => [
                              AdminOverviewTileID.DIRECTORY,
                              buildTileResult(
                                  AdminOverviewTileState.READY,
                                  scopeValues.noTelephonyServer
                                      ? getNoTelephonyServerSummary(language)
                                      : language.get(AdminOverviewLanguage.AdminOverview_Tile_Directory_Summary),
                                  scopeValues.noTelephonyServer ? undefined : getResponseTotal(response),
                                  getDirectoryAdditionalInfo(response, scopeValues)
                              )
                          ]
                      )
                  ]
                : [])
        ]

        Promise.allSettled(requests).then(
            (results: PromiseSettledResult<readonly [AdminOverviewTileID, TileResult]>[]) => {
                if (!isActive) return

                setTileResults(
                    (currentResults: TileResults): TileResults =>
                        results.reduce(
                            (
                                accumulatedResults: TileResults,
                                result: PromiseSettledResult<readonly [AdminOverviewTileID, TileResult]>
                            ) => {
                                if (result.status === 'fulfilled') {
                                    const [tileID, tileResult]: readonly [AdminOverviewTileID, TileResult] =
                                        result.value

                                    return {
                                        ...accumulatedResults,
                                        [tileID]: tileResult
                                    }
                                }

                                return accumulatedResults
                            },
                            {
                                ...currentResults,
                                [AdminOverviewTileID.CME]:
                                    currentResults[AdminOverviewTileID.CME].state === AdminOverviewTileState.LOADING
                                        ? buildTileResult(
                                              AdminOverviewTileState.ERROR,
                                              language.get(AdminOverviewLanguage.AdminOverview_State_Error)
                                          )
                                        : currentResults[AdminOverviewTileID.CME],
                                [AdminOverviewTileID.TELSYS]:
                                    currentResults[AdminOverviewTileID.TELSYS].state === AdminOverviewTileState.LOADING
                                        ? buildTileResult(
                                              AdminOverviewTileState.ERROR,
                                              language.get(AdminOverviewLanguage.AdminOverview_State_Error)
                                          )
                                        : currentResults[AdminOverviewTileID.TELSYS],
                                [AdminOverviewTileID.PARTITIONS]:
                                    currentResults[AdminOverviewTileID.PARTITIONS].state ===
                                    AdminOverviewTileState.LOADING
                                        ? buildTileResult(
                                              AdminOverviewTileState.ERROR,
                                              language.get(AdminOverviewLanguage.AdminOverview_State_Error)
                                          )
                                        : currentResults[AdminOverviewTileID.PARTITIONS],
                                [AdminOverviewTileID.USERS]:
                                    currentResults[AdminOverviewTileID.USERS].state === AdminOverviewTileState.LOADING
                                        ? buildTileResult(
                                              AdminOverviewTileState.ERROR,
                                              language.get(AdminOverviewLanguage.AdminOverview_State_Error)
                                          )
                                        : currentResults[AdminOverviewTileID.USERS],
                                [AdminOverviewTileID.DEVICES]:
                                    currentResults[AdminOverviewTileID.DEVICES].state === AdminOverviewTileState.LOADING
                                        ? buildTileResult(
                                              AdminOverviewTileState.ERROR,
                                              language.get(AdminOverviewLanguage.AdminOverview_State_Error)
                                          )
                                        : currentResults[AdminOverviewTileID.DEVICES],
                                [AdminOverviewTileID.AGENTS]:
                                    currentResults[AdminOverviewTileID.AGENTS].state === AdminOverviewTileState.LOADING
                                        ? buildTileResult(
                                              AdminOverviewTileState.ERROR,
                                              language.get(AdminOverviewLanguage.AdminOverview_State_Error)
                                          )
                                        : currentResults[AdminOverviewTileID.AGENTS],
                                [AdminOverviewTileID.DIRECTORY]:
                                    currentResults[AdminOverviewTileID.DIRECTORY].state ===
                                    AdminOverviewTileState.LOADING
                                        ? buildTileResult(
                                              AdminOverviewTileState.ERROR,
                                              language.get(AdminOverviewLanguage.AdminOverview_State_Error)
                                          )
                                        : currentResults[AdminOverviewTileID.DIRECTORY],
                                [AdminOverviewTileID.CODES]:
                                    currentResults[AdminOverviewTileID.CODES].state === AdminOverviewTileState.LOADING
                                        ? buildTileResult(
                                              AdminOverviewTileState.ERROR,
                                              language.get(AdminOverviewLanguage.AdminOverview_State_Error)
                                          )
                                        : currentResults[AdminOverviewTileID.CODES],
                                [AdminOverviewTileID.RECORDING]:
                                    currentResults[AdminOverviewTileID.RECORDING].state ===
                                    AdminOverviewTileState.LOADING
                                        ? buildTileResult(
                                              AdminOverviewTileState.ERROR,
                                              language.get(AdminOverviewLanguage.AdminOverview_State_Error)
                                          )
                                        : currentResults[AdminOverviewTileID.RECORDING]
                            }
                        )
                )
            }
        )

        return (): void => {
            isActive = false
        }
    }, [
        hasReceivedStableFilters,
        refreshCount,
        scopeValues.cmeId,
        scopeValues.includeCmeChildren,
        scopeValues.noTelephonyServer,
        scopeValues.partition,
        scopeValues.search,
        scopeValues.telephonyServer,
        tileAccess
    ])

    const renderTileValue = (tile: AdminOverviewTileViewModel): ReactElement => {
        if (tile.state === AdminOverviewTileState.LOADING) {
            return (
                <span className='admin-overview-page__tile-spinner-wrapper'>
                    <CircularProgress
                        color='inherit'
                        size={TILE_SPINNER_SIZE}
                        thickness={TILE_SPINNER_THICKNESS}
                        className='admin-overview-page__tile-spinner'
                    />
                </span>
            )
        }

        if (!isStringNotEmpty(tile.value ?? EMPTY_STRING)) {
            return (
                <span className='admin-overview-page__tile-value admin-overview-page__tile-value--fallback'>
                    {tile.summary}
                </span>
            )
        }

        return <span className='admin-overview-page__tile-value'>{tile.value}</span>
    }

    const renderTileAdditionalInfo = (tile: AdminOverviewTileViewModel): Nullable<ReactElement> => {
        if (
            tile.id === AdminOverviewTileID.DIRECTORY ||
            tile.additionalInfo.length === 0 ||
            tile.state !== AdminOverviewTileState.READY
        ) {
            return null
        }

        return (
            <div className='admin-overview-page__tile-additional-info'>
                {tile.additionalInfo.map(
                    (info: AdminOverviewTileAdditionalInfo): ReactElement => (
                        <div
                            className='admin-overview-page__tile-additional-info-row'
                            key={`${tile.id}-${info.label}-${info.value}`}
                        >
                            <span className='admin-overview-page__tile-additional-info-label'>{info.label}</span>
                            <span className='admin-overview-page__tile-additional-info-value'>{info.value}</span>
                        </div>
                    )
                )}
            </div>
        )
    }

    const renderTile = (tile: AdminOverviewTileViewModel): ReactElement => {
        const hasNumericValue: boolean = isStringNotEmpty(tile.value ?? EMPTY_STRING)

        const content: ReactElement = (
            <Paper
                elevation={0}
                className={`admin-overview-page__tile${tile.enabled ? EMPTY_STRING : TILE_DISABLED_CLASS_NAME}`}
            >
                <div className='admin-overview-page__tile-body'>
                    <Typography variant='subtitle2' className='admin-overview-page__tile-title'>
                        {tile.title}
                    </Typography>
                    <div className='admin-overview-page__tile-content-row'>
                        <div className='admin-overview-page__tile-value-group'>{renderTileValue(tile)}</div>
                        {hasNumericValue ? renderTileAdditionalInfo(tile) : null}
                    </div>
                    {hasNumericValue ? <span className='admin-overview-page__tile-summary'>{tile.summary}</span> : null}
                </div>
                <div className='admin-overview-page__tile-backdrop' aria-hidden='true'>
                    <Icon iconName={tile.icon} iconSize={IconSize.GIANT} className='admin-overview-page__tile-icon' />
                </div>
            </Paper>
        )

        if (!tile.enabled) return content

        if ('to' in tile && isVarNotEmpty(tile.to)) {
            return (
                <Link to={tile.to} aria-label={tile.ariaLabel} className={TILE_LINK_CLASS_NAME}>
                    {content}
                </Link>
            )
        }

        if ('href' in tile && isVarNotEmpty(tile.href)) {
            return (
                <a href={tile.href} aria-label={tile.ariaLabel} className={TILE_LINK_CLASS_NAME}>
                    {content}
                </a>
            )
        }

        return content
    }

    return (
        <div className='admin-overview-page'>
            <PageTitle title={RouterLang.PageTitle_admin}></PageTitle>
            <Typography variant='body1' color={TYPOGRAPHY_COLOR_TEXT_SECONDARY} className='admin-overview-page__intro'>
                {language.get(AdminOverviewLanguage.AdminOverview_Intro)}
            </Typography>
            <AdminFilters
                localeText={localeText}
                optionsToDisplay={ADMIN_OVERVIEW_FILTER_OPTIONS}
                onChange={handleFiltersChange}
                onRefresh={handleRefresh}
                urlBootstrap={urlBootstrap}
            />
            <Typography variant='body2' color={TYPOGRAPHY_COLOR_TEXT_SECONDARY} className='admin-overview-page__search-help'>
                {language.get(AdminOverviewLanguage.AdminOverview_Search_Helper)}
            </Typography>
            <Grid container spacing={TILE_GRID_SPACING} className='admin-overview-page__tiles'>
                {tiles.map(
                    (tile: AdminOverviewTileViewModel): ReactElement => (
                        <Grid item xs={TILE_GRID_XS} sm={TILE_GRID_SM} md={TILE_GRID_MD} key={tile.id}>
                            {renderTile(tile)}
                        </Grid>
                    )
                )}
            </Grid>
        </div>
    )
}
